/* ==========================================================================
   NOCO — The No Store · Foundation JS
   Vanilla, zero-dependency runtime for the NOCO Horizon theme.
   Implements the full data-attribute JS API used by every NOCO section:
     • Colourway theming (reads #noco-colourways JSON, sets --acc vars,
       updates [data-noco-field*], toggles .is-active on [data-noco-colour])
     • Shopify AJAX cart (add / change) + drawer render + free-ship bar
     • Single-open accordions
     • PDP size selection (variant id + error + stock note)
     • Quantity stepper (clamp 1–9)
     • Size-guide modal
     • Mobile menu
   All wiring is event-delegated on `document`, so it works for any section
   rendered anywhere (incl. Theme Editor section loads). Everything guards
   for missing elements. No framework, no build step, no dependencies.

   MONEY: every JS-rendered amount is formatted as ₹ + en-IN grouping from
   paise (value / 100). This is authoritative and matches across cart lines,
   subtotal, free-ship copy and the configurator price/mrp fields so the whole
   store reads consistently regardless of the store's Shopify money format.

   Colourway data source (#noco-colourways, emitted once globally):
     id, name, vibe, swatch, acc, deep, wash      (fixed design tokens)
     url, price(paise), priceMoney, compareAt(paise), compareAtMoney,
     variantId (first available), available
   Fields the JSON does NOT carry (rating / count / num) are read, when a
   section needs them updated on colour change, from data-* attributes on the
   matching [data-noco-colour] trigger (data-rating / data-count / data-num).

   State / class conventions this file OWNS (sections + noco-store.css match):
     [data-noco-colour].is-active ........ active swatch / row / tile
     [data-noco-cart-drawer].is-open ..... cart drawer / overlay shown
     [data-noco-acc].is-open ............. accordion row expanded
        └ [data-noco-acc-panel][hidden] .. panel toggled (hidden by default)
     [data-noco-size].is-active .......... selected PDP size
     [data-noco-size-err][hidden] ........ size error (hidden until ATC w/o size)
     [data-noco-stock-note][hidden] ...... low-stock note (hidden until relevant)
     [data-noco-sizeguide].is-open ....... size-guide modal shown
     [data-noco-menu].is-open ............ mobile menu shown
     [data-noco-cart-badge][hidden] ...... header count badge hidden when empty
   ========================================================================== */
(function () {
  'use strict';

  /* ---- constants -------------------------------------------------------- */
  var FREE_SHIP = 299900;       // ₹2,999 free-shipping threshold, in paise
  var DEAL_CENTS = 40000;       // ₹400 first-order coupon (WELCOME400), in paise
  var QTY_MIN = 1, QTY_MAX = 9;

  /* ---- haptics ----------------------------------------------------------
     A short tick when an interactive control is tapped, the way native/Shopify
     apps feel. navigator.vibrate is supported on Android (Chrome/Firefox);
     iOS Safari has no vibration API, so this is a silent no-op there — there
     is no web API to trigger the iPhone taptic engine. Honour reduced-motion. */
  var CAN_VIBRATE = typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function';
  var REDUCE_MOTION = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  function haptic(ms) {
    if (!CAN_VIBRATE || REDUCE_MOTION) return;
    try { navigator.vibrate(ms); } catch (e) {}
  }
  // Capture phase so the tick still fires for handlers that preventDefault().
  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || typeof t.closest !== 'function') return;
    // commit actions (add to cart / checkout) get a firmer pulse than a plain tap
    if (t.closest('[data-noco-atc],[data-noco-add],[data-noco-buy],[data-noco-checkout],[data-noco-xsell],button[name="checkout"]')) {
      haptic(18);
      return;
    }
    if (t.closest('button,a[href],[data-noco-colour],[data-noco-size],[data-noco-qty-inc],[data-noco-qty-dec],[data-noco-acc-trigger],[role="button"]')) {
      haptic(10);
    }
  }, true);
  var SIGN_OPEN = '−';     // − minus sign (pairs with +, not a dash)
  var SIGN_CLOSED = '+';
  // Title-keyword match order (crimson BEFORE cobalt — see build contract:
  // the Crimson product's handle wrongly contains "cobalt-blue-…").
  var TITLE_KEYWORDS = ['black', 'crimson', 'cobalt', 'forest', 'cream'];
  // The ⌀ mark used inside cart-line placeholder tiles (matches prototype).
  var MARK_SVG =
    '<svg width="22" height="22" viewBox="0 0 40 40" aria-hidden="true">' +
    '<circle cx="20" cy="20" r="14" fill="none" stroke="#fff" stroke-width="4"></circle>' +
    '<line x1="25" y1="8" x2="15" y2="32" stroke="#fff" stroke-width="4" stroke-linecap="round"></line></svg>';
  var CART_EMPTY_HTML =
    '<div style="text-align:center;padding:60px 10px;color:#8a8371;">' +
    '<div style="font-weight:800;font-size:16px;color:#0E0E0F;margin-bottom:6px;">Your bag is empty</div>' +
    '<div style="font-size:13.5px;">Add a pair from the range above.</div></div>';

  /* ---- module state ----------------------------------------------------- */
  var COLOURWAYS = {};   // id -> colourway object (from JSON)
  var COLOUR_ORDER = []; // ids in emit order
  var STATE = { colour: null, selectedVariantId: null, selectedUk: null, qty: 1 };

  /* ---- tiny helpers ----------------------------------------------------- */
  function $(sel, ctx) { return (ctx || document).querySelector(sel); }
  function $$(sel, ctx) {
    return Array.prototype.slice.call((ctx || document).querySelectorAll(sel));
  }
  function pad2(n) { return ('0' + n).slice(-2); }
  function clamp(n, lo, hi) { return Math.max(lo, Math.min(hi, n)); }
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function has(v) { return v != null && v !== ''; }
  // Money: paise -> "₹" + en-IN grouped rupees (2 decimals only when needed).
  function money(paise) {
    var n = Number(paise) || 0;
    var hasPaise = Math.round(n) % 100 !== 0;
    return '₹' + (n / 100).toLocaleString('en-IN', {
      minimumFractionDigits: hasPaise ? 2 : 0,
      maximumFractionDigits: hasPaise ? 2 : 0
    });
  }
  // Map any cart line's product title to its colourway accent for the tile.
  function tileColour(title) {
    var t = (title || '').toLowerCase();
    for (var i = 0; i < TITLE_KEYWORDS.length; i++) {
      var kw = TITLE_KEYWORDS[i];
      if (t.indexOf(kw) >= 0 && COLOURWAYS[kw] && COLOURWAYS[kw].acc) return COLOURWAYS[kw].acc;
    }
    return '#0E0E0F';
  }
  // First [data-noco-colour] element whose value equals id (avoids selector
  // escaping; ids are simple slugs). Used to read section-supplied data-*.
  function firstColourEl(id) {
    var els = $$('[data-noco-colour]');
    for (var i = 0; i < els.length; i++) {
      if (els[i].getAttribute('data-noco-colour') === id) return els[i];
    }
    return null;
  }

  /* ======================================================================
     COLOURWAY THEMING
     ====================================================================== */
  function loadColourways() {
    var el = document.getElementById('noco-colourways');
    if (!el) return;
    var data;
    try { data = JSON.parse(el.textContent || el.innerHTML || 'null'); }
    catch (err) { console.warn('[noco] colourways JSON parse failed', err); return; }
    if (!data) return;
    var list;
    if (Array.isArray(data)) list = data;
    else if (Array.isArray(data.colourways)) list = data.colourways;
    else list = Object.keys(data).map(function (k) { var o = data[k] || {}; if (o.id == null) o.id = k; return o; });
    COLOURWAYS = {};
    COLOUR_ORDER = [];
    list.forEach(function (c, i) {
      if (!c || c.id == null) return;
      c._i = i;
      COLOURWAYS[c.id] = c;
      COLOUR_ORDER.push(c.id);
    });
  }

  // Merge the authoritative JSON colourway with any supplemental fields a
  // section exposes on the matching [data-noco-colour] trigger (data-rating,
  // data-count, data-num, data-vibe, data-variant-id, …). JSON always wins.
  function colourData(id) {
    var c = COLOURWAYS[id] || { id: id, _i: 0 };
    var el = firstColourEl(id);
    var ds = (el && el.dataset) ? el.dataset : {};
    function fromEither(cKey, dsKey) {
      if (has(c[cKey])) return c[cKey];
      if (has(ds[dsKey])) return ds[dsKey];
      return null;
    }
    return {
      id: id,
      _i: c._i || 0,
      acc: c.acc, deep: c.deep, wash: c.wash,
      name: c.name || ds.name || '',
      vibe: c.vibe || ds.vibe || '',
      image: c.image || ds.image || '',
      rangeImage: c.rangeImage || ds.rangeImage || '',
      num: fromEither('num', 'num'),
      price: has(c.price) ? c.price : null,
      priceMoney: c.priceMoney || ds.priceMoney || null,
      compareAt: has(c.compareAt) ? c.compareAt : null,
      compareAtMoney: c.compareAtMoney || ds.compareAtMoney || null,
      rating: fromEither('rating', 'rating'),
      count: fromEither('count', 'count'),
      url: c.url || ds.url || '',
      variantId: has(c.variantId) ? c.variantId
        : (has(c.variant_id) ? c.variant_id
          : (has(ds.variantId) ? ds.variantId : null))
    };
  }

  function applyTheme(d) {
    if (!d) return;
    var r = document.documentElement;
    if (d.acc) r.style.setProperty('--acc', d.acc);
    if (d.deep) r.style.setProperty('--acc-deep', d.deep);
    if (d.wash) r.style.setProperty('--acc-wash', d.wash);
  }

  function fieldMap(d) {
    return {
      name: d.name || '',
      nameUpper: (d.name || '').toUpperCase(),
      num: has(d.num) ? String(d.num) : pad2((d._i || 0) + 1),
      // Prefer money(paise) so configurator prices match the cart exactly;
      // fall back to the Shopify-formatted string only when paise is absent.
      price: d.price != null ? money(d.price) : (d.priceMoney || ''),
      // Compare-at/MRP strike removed store-wide: pricing shows a single price,
      // the first-order deal lives only in the WELCOME400 claim widget.
      mrp: '',
      rating: has(d.rating) ? String(d.rating) : '',
      count: has(d.count) ? String(d.count) : '',
      vibe: d.vibe || '',
      image: d.image || '',
      rangeImage: d.rangeImage || '',
      addUrl: d.url || '',
      addVariant: has(d.variantId) ? String(d.variantId) : ''
    };
  }

  function updateFields(d) {
    if (!d) return;
    var f = fieldMap(d);
    $$('[data-noco-field]').forEach(function (el) {
      var key = el.getAttribute('data-noco-field');
      if (!(key in f)) return;
      var v = f[key];
      if (key === 'image' || key === 'rangeImage') {
        if (el.tagName === 'IMG' && v) el.setAttribute('src', v);
        return;
      }
      if (key === 'addVariant') {
        // Keep the AJAX add button's variant in sync; clear it when the
        // selected colour has no purchasable variant so we never add stale.
        if (v) el.setAttribute('data-variant-id', v);
        else el.removeAttribute('data-variant-id');
        return;
      }
      if (key === 'addUrl') {
        if (el.tagName === 'A') { if (v) el.setAttribute('href', v); }
        else { el.textContent = v; }
        return;
      }
      el.textContent = v;
    });
  }

  function markColourActive(id) {
    $$('[data-noco-colour]').forEach(function (el) {
      var on = el.getAttribute('data-noco-colour') === id;
      el.classList.toggle('is-active', on);
      if (el.hasAttribute('aria-pressed')) el.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
  }

  function applyColour(id) {
    if (id == null || id === '') return;
    STATE.colour = id;
    // Root theming hook uses a DIFFERENT attribute than the click triggers:
    // '[data-noco-colour]' on <html> would make closest() match on EVERY page
    // click and preventDefault ALL links (menu, cards). Never reuse that name here.
    if (document.documentElement) document.documentElement.setAttribute('data-noco-active-colour', id);
    var d = colourData(id);
    applyTheme(d);
    updateFields(d);
    markColourActive(id);
    document.dispatchEvent(new CustomEvent('noco:colour', { detail: { id: id } }));
  }

  function initColour() {
    // PDP: current colour declared server-side; swatches are navigation links.
    var pdp = $('[data-noco-pdp-colour]');
    if (pdp) { applyColour(pdp.getAttribute('data-noco-pdp-colour')); return; }
    // Homepage / configurator: default attr -> pre-marked active -> first colour.
    var def = $('[data-noco-default-colour]');
    var id = def ? def.getAttribute('data-noco-default-colour') : null;
    if (!id) { var a = $('[data-noco-colour].is-active'); if (a) id = a.getAttribute('data-noco-colour'); }
    if (!id && COLOUR_ORDER.length) id = COLOUR_ORDER[0];
    if (id) applyColour(id);
  }

  /* ======================================================================
     CART (Shopify AJAX)
     ====================================================================== */
  function cartLineHTML(item) {
    var title = esc(item.product_title || item.title || '');
    var variant = (item.variant_title && item.variant_title !== 'Default Title')
      ? esc(item.variant_title) : '';
    var unit = money(item.final_price != null ? item.final_price : item.price);
    var qty = item.quantity;
    var tile = tileColour(item.product_title || item.title || '');
    var key = esc(item.key || '');
    // Shopify's /cart.js AJAX response gives the line image as a plain URL
    // string on item.image (not item.featured_image like the Liquid object) —
    // always prefer the real listing photo; the mark is only a last resort.
    var imgUrl = item.image || (item.featured_image && item.featured_image.url) || '';
    var thumb = imgUrl
      ? '<img src="' + esc(imgUrl) + '" alt="' + title + '" width="60" height="60" style="width:100%;height:100%;object-fit:cover;display:block;">'
      : MARK_SVG;
    return '' +
      '<div style="display:flex;gap:12px;padding:14px 0;border-bottom:1px solid #E7E4DC;">' +
        '<div style="width:60px;height:60px;background:' + tile + ';flex:0 0 auto;display:flex;align-items:center;justify-content:center;overflow:hidden;">' + thumb + '</div>' +
        '<div style="flex:1;min-width:0;">' +
          '<div style="font-weight:800;font-size:14.5px;">' + title + '</div>' +
          (variant ? '<div style="font-size:12px;color:#8a8371;font-family:\'Space Grotesk\',sans-serif;">' + variant + '</div>' : '') +
          '<div style="display:flex;align-items:center;gap:12px;margin-top:6px;">' +
            '<div style="display:flex;align-items:center;border:1.5px solid #0E0E0F;">' +
              '<button type="button" data-noco-line-dec data-key="' + key + '" data-qty="' + qty + '"' + (qty <= 1 ? ' disabled' : '') + ' aria-label="Decrease quantity" style="width:28px;height:28px;background:none;border:none;cursor:pointer;font-size:15px;line-height:1;' + (qty <= 1 ? 'color:#cfc7b8;cursor:not-allowed;' : 'color:#0E0E0F;') + '">−</button>' +
              '<span data-noco-line-qty style="min-width:22px;text-align:center;font-family:\'Space Grotesk\',sans-serif;font-weight:700;font-size:13px;">' + qty + '</span>' +
              '<button type="button" data-noco-line-inc data-key="' + key + '" data-qty="' + qty + '"' + (qty >= QTY_MAX ? ' disabled' : '') + ' aria-label="Increase quantity" style="width:28px;height:28px;background:none;border:none;cursor:pointer;font-size:15px;line-height:1;color:#0E0E0F;">+</button>' +
            '</div>' +
            '<div style="font-weight:700;font-size:14px;">' + unit + '</div>' +
          '</div>' +
          '<div style="font-family:\'Space Grotesk\',sans-serif;font-size:10.5px;letter-spacing:.04em;color:#0F9E76;margin-top:4px;">HANDMADE IN INDIA · EASY 30-DAY RETURNS</div>' +
        '</div>' +
        '<button type="button" data-noco-cart-remove data-key="' + key + '" style="background:none;border:none;color:#a49c8b;cursor:pointer;font-size:12px;align-self:flex-start;">Remove</button>' +
      '</div>';
  }

  function renderCart(cart) {
    if (!cart) return;
    STATE.cart = cart; // cache: lets the upsell tiles skip a /cart.js round trip
    var count = cart.item_count || 0;
    var total = cart.total_price || 0;

    // Header badge + drawer count share the [data-noco-cart-count] hook.
    $$('[data-noco-cart-count]').forEach(function (el) { el.textContent = count; });
    $$('[data-noco-cart-badge]').forEach(function (el) {
      el.hidden = count === 0;
      el.classList.toggle('is-empty', count === 0);
    });

    var itemsHTML = count === 0
      ? CART_EMPTY_HTML
      : (cart.items || []).map(cartLineHTML).join('');
    $$('[data-noco-cart-items]').forEach(function (el) { el.innerHTML = itemsHTML; });

    /* Coupon line. Read the REAL cart, never assume: total_price is already net of
       any discount Shopify has attached, so the old `total − DEAL_CENTS` subtracted
       the ₹400 a second time (₹4,000 → showed ₹3,600 subtotal, −₹400, "pay ₹3,200").
       Two honest states:
         applied  — Shopify holds the code: subtotal = pre-discount, line = the real
                    allocated amount, payable = total_price.
         expected — shopper copied the code but hasn't pasted it at checkout yet:
                    subtotal = total, and we label the ₹400 as still to come. */
    var applied = (cart.cart_level_discount_applications || [])
      .map(function (d) { return { title: d.title || '', amount: d.total_allocated_amount || 0 }; })
      .filter(function (d) { return d.amount > 0; });
    var appliedTotal = applied.reduce(function (s, d) { return s + d.amount; }, 0);
    var gross = cart.original_total_price != null && cart.original_total_price > 0
      ? cart.original_total_price
      : total + appliedTotal;

    /* Reconcile the "applied" look against the REAL cart. The PDP sets
       html.noco-deal-on optimistically from a localStorage flag so there's no flash;
       if Shopify isn't actually holding a discount (code removed, expired, per-customer
       limit hit, cart emptied) we clear it here so a stale flag can never show a
       slashed price the shopper won't be charged. */
    if (count > 0 && appliedTotal > 0) {
      document.documentElement.classList.add('noco-deal-on');
      try { localStorage.setItem('nocoDealClaimed', '1'); } catch (e) {}
    } else if (count > 0) {
      document.documentElement.classList.remove('noco-deal-on');
      try { localStorage.removeItem('nocoDealClaimed'); } catch (e) {}
    }

    var copied = false;
    try { copied = localStorage.getItem('nocoDealCopied') === '1'; } catch (e) {}

    $$('[data-noco-cart-subtotal]').forEach(function (el) {
      el.textContent = money(appliedTotal > 0 ? gross : total);
    });

    $$('[data-noco-cart-deal]').forEach(function (el) {
      var amt = el.querySelector('[data-noco-cart-deal-amt]');
      var est = el.querySelector('[data-noco-cart-deal-est]');
      var lbl = el.querySelector('[data-noco-cart-deal-label]');
      var estLbl = el.querySelector('[data-noco-cart-deal-est-label]');

      if (appliedTotal > 0) {                      // real discount on the cart
        el.hidden = false;
        if (lbl) lbl.textContent = (applied[0].title || 'Discount') + ' · applied';
        if (amt) amt.textContent = '−' + money(appliedTotal);
        if (estLbl) estLbl.textContent = 'You pay';
        if (est) est.textContent = money(total);
        return;
      }
      if (copied && count > 0 && total > DEAL_CENTS) {   // copied, not pasted yet
        el.hidden = false;
        if (lbl) lbl.textContent = 'WELCOME400 · paste at checkout';
        if (amt) amt.textContent = '−' + money(DEAL_CENTS);
        if (estLbl) estLbl.textContent = 'You pay with code';
        if (est) est.textContent = money(total - DEAL_CENTS);
        return;
      }
      el.hidden = true;
    });

    // Every pair is above the old ₹2,999 threshold, so the progress message was
    // always "unlocked" anyway — and the bare number was being misread as the
    // product price. State the benefit; keep the bar full.
    $$('[data-noco-ship-msg]').forEach(function (el) { el.textContent = 'FREE shipping on all orders'; });
    $$('[data-noco-ship-bar]').forEach(function (el) { el.style.width = '100%'; });
  }

  function refreshCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) { renderCart(cart); return cart; })
      .catch(function (err) { console.warn('[noco] cart fetch failed', err); });
  }

  // The PDP fires this after applying the coupon so the drawer picks up the
  // discount Shopify just attached, instead of showing a stale total.
  document.addEventListener('noco:deal', function () { refreshCart(); });

  function addToCart(id, qty, btn, opts) {
    opts = opts || {};
    var open = opts.open !== false;
    if (!id) return Promise.resolve(false);
    if (btn) { btn.setAttribute('data-loading', ''); btn.disabled = true; }
    var vid = Number(id); if (isNaN(vid)) vid = id;
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: vid, quantity: qty || 1 }] })
    })
      .then(function (r) {
        if (!r.ok) return r.json().then(function (e) { throw e; });
        return r.json();
      })
      .then(function () {
        // Open the drawer as soon as the add is CONFIRMED — don't hold it
        // hostage to the follow-up /cart.js render. The totals pop in ~300ms
        // later; perceived speed roughly doubles.
        if (open) openCart();
        return refreshCart();
      })
      .then(function () { return true; })
      .catch(function (err) {
        document.dispatchEvent(new CustomEvent('noco:cart:error', { detail: err }));
        console.warn('[noco] add to cart failed', (err && err.description) || err);
        return false;
      })
      .then(function (res) {
        if (btn) { btn.removeAttribute('data-loading'); btn.disabled = false; }
        return res;
      });
  }

  function removeLine(el) {
    var key = el.getAttribute('data-key');
    if (!key) return;
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: 0 })
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) { renderCart(cart); })
      .catch(function (err) { console.warn('[noco] remove line failed', err); });
  }

  /* Drawer line qty stepper. Optimistic: the number updates the instant you
     tap; the authoritative /cart/change.js response then re-renders the drawer
     (renderCart), which also refreshes totals + the free-ship bar. A per-line
     in-flight lock stops double-taps racing two change calls (Shopify would
     process both and land on the wrong qty). */
  var lineQtyBusy = {};
  function changeLineQty(el, dir) {
    var key = el.getAttribute('data-key');
    if (!key || lineQtyBusy[key]) return;
    var qty = parseInt(el.getAttribute('data-qty'), 10) || 1;
    var next = clamp(qty + dir, 1, QTY_MAX);
    if (next === qty) return;
    lineQtyBusy[key] = true;
    // optimistic paint on this line only
    var row = el.closest('div[style*="border-bottom"]');
    if (row) {
      var qEl = row.querySelector('[data-noco-line-qty]');
      if (qEl) qEl.textContent = next;
    }
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: next })
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) { renderCart(cart); })
      .catch(function (err) { console.warn('[noco] qty change failed', err); refreshCart(); })
      .then(function () { delete lineQtyBusy[key]; });
  }

  function openCart() {
    $$('[data-noco-cart-drawer]').forEach(function (d) {
      d.classList.add('is-open');
      d.setAttribute('aria-hidden', 'false');
    });
    updateScrollLock();
  }
  function closeCart() {
    $$('[data-noco-cart-drawer]').forEach(function (d) {
      d.classList.remove('is-open');
      d.setAttribute('aria-hidden', 'true');
    });
    updateScrollLock();
  }

  /* ======================================================================
     ACCORDION (single-open per [data-noco-accordion])
     ====================================================================== */
  function setAcc(row, open) {
    row.classList.toggle('is-open', open);
    var panel = row.querySelector('[data-noco-acc-panel]');
    if (panel) panel.hidden = !open;
    var trig = row.querySelector('[data-noco-acc-trigger]');
    if (trig) trig.setAttribute('aria-expanded', open ? 'true' : 'false');
    var sign = row.querySelector('[data-noco-acc-sign]');
    if (sign) sign.textContent = open ? SIGN_OPEN : SIGN_CLOSED;
  }
  function handleAcc(trigger) {
    var row = trigger.closest('[data-noco-acc]');
    if (!row) return;
    var container = trigger.closest('[data-noco-accordion]');
    var isOpen = row.classList.contains('is-open');
    var rows = container
      ? container.querySelectorAll('[data-noco-acc]')
      : document.querySelectorAll('[data-noco-acc]');
    Array.prototype.forEach.call(rows, function (r) { setAcc(r, false); });
    if (!isOpen) setAcc(row, true); // single-open toggle
  }

  /* ======================================================================
     PDP SIZE SELECTION + ERROR + STOCK NOTE
     ====================================================================== */
  function showSizeErr() {
    $$('[data-noco-size-err]').forEach(function (el) { el.hidden = false; el.classList.add('is-visible'); });
    // On mobile the main grid's error text is usually scrolled off, so make the
    // sticky-bar dropdown itself shout: red border + shake (restarted every tap,
    // via reflow) + a buzz. A quiet border alone read as "the button is dead".
    $$('[data-noco-size-select]').forEach(function (sel) {
      sel.classList.remove('is-err');
      void sel.offsetWidth; // restart the shake animation
      sel.classList.add('is-err');
    });
    haptic([30, 40, 30]);
  }
  function hideSizeErr() {
    $$('[data-noco-size-err]').forEach(function (el) { el.hidden = true; el.classList.remove('is-visible'); });
    $$('[data-noco-size-select]').forEach(function (sel) { sel.classList.remove('is-err'); });
  }

  /* Sticky-bar size dropdown -> reuse selectSize() on the matching grid button so
     stock notes, labels, STATE and the grid highlight all stay in one code path. */
  document.addEventListener('change', function (e) {
    var sel = e.target;
    if (!sel || typeof sel.closest !== 'function') return;
    if (!sel.closest('[data-noco-size-select]')) return;
    var vid = sel.value;
    if (!vid) return;
    var btn = $('[data-noco-size][data-variant-id="' + vid + '"]');
    if (btn) selectSize(btn);
  });
  function setStockNote(text) {
    $$('[data-noco-stock-note]').forEach(function (el) {
      if (text) { el.textContent = text; el.hidden = false; }
      else { el.textContent = ''; el.hidden = true; }
    });
  }
  function selectSize(el) {
    if (el.getAttribute('data-available') === 'false' || el.getAttribute('data-stock') === 'out') return;
    // The same size is offered in TWO places (the main grid + the mobile sticky
    // bar), so activate EVERY chip for this variant — not just the tapped one —
    // or the other selector visibly falls out of sync.
    var vid = el.getAttribute('data-variant-id');
    $$('[data-noco-size]').forEach(function (s) {
      var on = s.getAttribute('data-variant-id') === vid;
      s.classList.toggle('is-active', on);
      if (s.hasAttribute('aria-pressed')) s.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    // Mirror into the sticky-bar dropdown so picking from the main grid updates it too.
    $$('[data-noco-size-select]').forEach(function (sel) {
      if (sel.value !== vid) sel.value = vid;
      sel.classList.remove('is-err');
    });
    STATE.selectedVariantId = el.getAttribute('data-variant-id');
    STATE.selectedUk = el.getAttribute('data-uk');
    hideSizeErr();
    if (el.getAttribute('data-stock') === 'low') {
      var n = el.getAttribute('data-stock-count') || '3';
      setStockNote('Only ' + n + ' left in UK ' + (el.getAttribute('data-uk') || ''));
    } else {
      setStockNote('');
    }
    // Keep the mobile sticky-bar size label in sync.
    $$('[data-noco-size-label]').forEach(function (l) {
      l.textContent = 'UK ' + (el.getAttribute('data-uk') || '') + ' selected';
    });
  }
  function getSelectedVariant() {
    var active = $('[data-noco-size].is-active');
    return active ? active.getAttribute('data-variant-id') : null;
  }

  /* ======================================================================
     QTY STEPPER (clamp 1–9)
     ====================================================================== */
  function qtyStep(el, dir) {
    var wrap = el.closest('[data-noco-qty]');
    if (!wrap) return;
    var val = wrap.querySelector('[data-noco-qty-val]');
    if (!val) return;
    var n = parseInt(val.textContent, 10);
    if (isNaN(n)) n = 1;
    n = clamp(n + dir, QTY_MIN, QTY_MAX);
    val.textContent = n;
    STATE.qty = n;
  }
  function currentQty() {
    var v = $('[data-noco-qty-val]');
    var n = v ? parseInt(v.textContent, 10) : 1;
    if (isNaN(n)) n = 1;
    return clamp(n, QTY_MIN, QTY_MAX);
  }

  /* ======================================================================
     ADD HANDLERS
     ====================================================================== */
  function handleAdd(el) {
    // Generic quick-add (homepage range / pair-it): variant baked on the button.
    var vid = el.getAttribute('data-variant-id');
    if (!vid) return;
    var qty = parseInt(el.getAttribute('data-qty'), 10) || 1;
    addToCart(vid, qty, el);
  }
  function handleAtc(el, buyNow) {
    // PDP main / sticky ATC: needs the selected size's variant.
    var sizes = $$('[data-noco-size]');
    var variant = getSelectedVariant();
    if (sizes.length && !variant) { showSizeErr(); return; }
    if (!variant) variant = el.getAttribute('data-variant-id');
    if (!variant) return;
    addToCart(variant, currentQty(), el, { open: !buyNow }).then(function (ok) {
      if (!ok || !buyNow) return;
      // Buy Now: route through the GoKwik-decorated cart checkout button so the
      // one-click-checkout popup intercepts it. GoKwik only hooks name="checkout"
      // / a[href*="/checkout"] (marking them [data-gokwik-processed]) — it never
      // sees our custom nav, which is why Buy Now bypassed it. If GoKwik isn't
      // active/decorated, fall back to native checkout.
      var gkBtn = document.querySelector(
        'button[name="checkout"][data-gokwik-processed],' +
        'input[name="checkout"][data-gokwik-processed],' +
        'a[href*="/checkout"][data-gokwik-processed]'
      );
      if (gkBtn) { gkBtn.click(); return; }
      window.location.href = '/checkout';
    });
  }

  /* ======================================================================
     MODALS / MENU / SCROLL LOCK
     ====================================================================== */
  function anyOverlayOpen() {
    // menu excluded on purpose — the inline header script owns the menu's
    // scroll lock (html.noco-menu-open) so the two don't fight over overflow.
    return !!$('[data-noco-cart-drawer].is-open, [data-noco-sizeguide].is-open');
  }
  function updateScrollLock() {
    document.documentElement.style.overflow = anyOverlayOpen() ? 'hidden' : '';
  }
  function openSizeGuide() {
    $$('[data-noco-sizeguide]').forEach(function (m) { m.classList.add('is-open'); m.setAttribute('aria-hidden', 'false'); });
    updateScrollLock();
  }
  function closeSizeGuide() {
    $$('[data-noco-sizeguide]').forEach(function (m) { m.classList.remove('is-open'); m.setAttribute('aria-hidden', 'true'); });
    updateScrollLock();
  }

  /* ======================================================================
     DELEGATED EVENTS  (document-level so any section works, anywhere)
     ====================================================================== */
  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || typeof t.closest !== 'function') return;
    var el;

    // One-click cross-sell (drawer "COMPLETE THE RANGE"): add the same size the
    // shopper already has in the bag; fall back to the PDP link when the bag is
    // empty or that size is unavailable in the other colourway.
    if ((el = t.closest('[data-noco-xsell]'))) {
      var xvRaw = el.getAttribute('data-variants');
      var xvMap = null;
      try { xvMap = JSON.parse(xvRaw || 'null'); } catch (err) { xvMap = null; }
      if (xvMap) {
        e.preventDefault();
        var xhref = el.getAttribute('href');
        // Use the cached cart (renderCart keeps it fresh) — the drawer these
        // tiles live in was itself rendered from it, so an extra /cart.js round
        // trip here only added latency.
        var pick = function (cart) {
          var size = cart && cart.items && cart.items.length
            ? String(cart.items[0].variant_title || '').trim() : '';
          var vid = size && xvMap[size];
          if (vid) { addToCart(vid, 1, el); }
          else if (xhref) { window.location.href = xhref; }
        };
        if (STATE.cart) { pick(STATE.cart); }
        else {
          fetch('/cart.js', { headers: { Accept: 'application/json' } })
            .then(function (r) { return r.json(); })
            .then(pick)
            .catch(function () { if (xhref) window.location.href = xhref; });
        }
      }
      return;
    }

    if ((el = t.closest('[data-noco-cart-open]'))) { e.preventDefault(); openCart(); return; }
    if ((el = t.closest('[data-noco-cart-close]'))) { e.preventDefault(); closeCart(); return; }
    if ((el = t.closest('[data-noco-line-dec]'))) { e.preventDefault(); changeLineQty(el, -1); return; }
    if ((el = t.closest('[data-noco-line-inc]'))) { e.preventDefault(); changeLineQty(el, 1); return; }
    if ((el = t.closest('[data-noco-cart-remove]'))) { e.preventDefault(); removeLine(el); return; }
    if ((el = t.closest('[data-noco-checkout]'))) { e.preventDefault(); window.location.href = '/checkout'; return; }

    if ((el = t.closest('[data-noco-add]'))) { e.preventDefault(); handleAdd(el); return; }
    if ((el = t.closest('[data-noco-atc]'))) { e.preventDefault(); handleAtc(el, false); return; }
    if ((el = t.closest('[data-noco-buy]'))) { e.preventDefault(); handleAtc(el, true); return; }

    if ((el = t.closest('[data-noco-size]'))) { selectSize(el); return; }
    if ((el = t.closest('[data-noco-qty-dec]'))) { e.preventDefault(); qtyStep(el, -1); return; }
    if ((el = t.closest('[data-noco-qty-inc]'))) { e.preventDefault(); qtyStep(el, 1); return; }

    if ((el = t.closest('[data-noco-acc-trigger]'))) { e.preventDefault(); handleAcc(el); return; }

    if ((el = t.closest('[data-noco-sizeguide-open]'))) { e.preventDefault(); openSizeGuide(); return; }
    if ((el = t.closest('[data-noco-sizeguide-close]'))) { e.preventDefault(); closeSizeGuide(); return; }

    // NOTE: the mobile menu is handled by an inline script in noco-header.liquid
    // so it works before this deferred bundle hydrates. Do not bind it here.

    if ((el = t.closest('[data-noco-gallery-thumb]'))) {
      e.preventDefault();
      var gfull = el.getAttribute('data-full');
      var gscope = el.closest('.noco-pdp-gallery') || document;
      var gmain = gscope.querySelector('[data-noco-gallery-main]');
      if (gmain && gfull) gmain.setAttribute('src', gfull);
      $$('[data-noco-gallery-thumb]', gscope).forEach(function (b) { b.classList.toggle('is-active', b === el); });
      return;
    }

    if ((el = t.closest('[data-noco-colour]'))) {
      // Guard: never treat <html>/<body> as a colour trigger — a root-level
      // attribute here would swallow every click on the page.
      if (el === document.documentElement || el === document.body) return;
      // Links inside a trigger (the range rows' SHOP NOW) and swatches that ARE
      // links (PDP / range tiles) must navigate normally.
      if (t.closest('a[href]')) return;
      if (el.tagName === 'A' && el.getAttribute('href')) return;
      e.preventDefault();
      applyColour(el.getAttribute('data-noco-colour'));
      return;
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' || e.keyCode === 27) {
      closeCart();
      closeSizeGuide();
      // menu Escape handled by the inline header script
    }
    // Range rows are div[role=button] (they contain a SHOP NOW link, so they
    // cannot be <button>s) — keep Enter/Space activation for keyboards.
    if (e.key === 'Enter' || e.key === ' ' || e.keyCode === 13 || e.keyCode === 32) {
      var kt = e.target;
      if (kt && typeof kt.closest === 'function') {
        var row = kt.closest('[data-noco-colour][role="button"]');
        if (row && kt.tagName !== 'A') {
          e.preventDefault();
          applyColour(row.getAttribute('data-noco-colour'));
        }
      }
    }
  });

  /* ======================================================================
     INIT
     ====================================================================== */
  /* ---- mobile sticky ATC: get out of the way at the footer ----------------
     Once the footer scrolls into view the buying decision is over, and the bar
     just covers the footer links. Slide it away while the footer is visible and
     bring it straight back on scroll-up. Idempotent: safe to re-run on section
     re-render (Shopify editor) because we tag the footer once it's observed. */
  function initStickyAtcFooterHide() {
    var bars = $$('.noco-mobile-sticky');
    if (!bars.length || typeof IntersectionObserver === 'undefined') return;
    var footer = document.querySelector('footer');
    if (!footer || footer.getAttribute('data-noco-atc-io')) return;
    footer.setAttribute('data-noco-atc-io', '1');
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        $$('.noco-mobile-sticky').forEach(function (bar) {
          bar.classList.toggle('is-hidden', e.isIntersecting);
        });
      });
    }, { threshold: 0 });
    io.observe(footer);
  }

  function init() {
    loadColourways();
    initColour();
    refreshCart(); // sync header badge + drawer with any existing cart
    initStickyAtcFooterHide();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  // Theme Editor: re-hydrate when a section is (re)inserted.
  document.addEventListener('shopify:section:load', function () {
    loadColourways();
    initColour();
    refreshCart();
  });
  // bfcache restore: cart may be stale.
  window.addEventListener('pageshow', function (e) { if (e.persisted) refreshCart(); });

  /* ---- optional programmatic API --------------------------------------- */
  window.NOCO = {
    setColour: applyColour,
    openCart: openCart,
    closeCart: closeCart,
    refreshCart: refreshCart,
    addToCart: addToCart,
    money: money
  };
})();
